import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";

export function QuietHoursPanel() {
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/push/quiet-hours")
      .then(({ data }) => setCfg(data))
      .catch(() => toast.error("Kunde inte hämta tysta timmar"))
      .finally(() => setLoading(false));
  }, []);

  const save = async (next) => {
    setSaving(true);
    try {
      const { data } = await api.put("/push/quiet-hours", {
        enabled: next.quiet_hours_enabled,
        start_hour: next.quiet_hours_start,
        end_hour: next.quiet_hours_end,
      });
      setCfg(data);
      toast.success("Sparat");
    } catch (e) {
      toast.error("Kunde inte spara");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <p className="text-xs text-[color:var(--text-tertiary)]">Laddar…</p>;
  if (!cfg) return null;

  return (
    <>
      <p className="text-xs text-[color:var(--text-secondary)] mb-3 leading-relaxed">
        Push-notiser köas under tysta timmar och skickas första morgonen efter de slutar.
        Förälder-mail (söndagsbriefing, nya prov/läxor) påverkas inte.
      </p>
      <div className="flex items-center justify-between mb-3">
        <span className="text-sm font-medium">Aktivera tysta timmar</span>
        <Switch
          checked={cfg.quiet_hours_enabled}
          onCheckedChange={(v) => save({ ...cfg, quiet_hours_enabled: v })}
          disabled={saving}
          data-testid="quiet-hours-toggle"
        />
      </div>
      <div className={`grid grid-cols-2 gap-3 ${!cfg.quiet_hours_enabled ? "opacity-50 pointer-events-none" : ""}`}>
        <label className="block">
          <span className="text-[10px] uppercase tracking-wider text-[color:var(--text-tertiary)]">Från (timme)</span>
          <select
            value={cfg.quiet_hours_start}
            onChange={(e) => save({ ...cfg, quiet_hours_start: parseInt(e.target.value, 10) })}
            disabled={saving}
            className="mt-1 w-full h-9 rounded-lg border border-[color:var(--border)] bg-white px-2 text-sm"
            data-testid="quiet-hours-start"
          >
            {Array.from({ length: 24 }, (_, i) => (
              <option key={i} value={i}>{i.toString().padStart(2, "0")}:00</option>
            ))}
          </select>
        </label>
        <label className="block">
          <span className="text-[10px] uppercase tracking-wider text-[color:var(--text-tertiary)]">Till (timme)</span>
          <select
            value={cfg.quiet_hours_end}
            onChange={(e) => save({ ...cfg, quiet_hours_end: parseInt(e.target.value, 10) })}
            disabled={saving}
            className="mt-1 w-full h-9 rounded-lg border border-[color:var(--border)] bg-white px-2 text-sm"
            data-testid="quiet-hours-end"
          >
            {Array.from({ length: 24 }, (_, i) => (
              <option key={i} value={i}>{i.toString().padStart(2, "0")}:00</option>
            ))}
          </select>
        </label>
      </div>
      <p className="mt-3 text-[11px] text-[color:var(--text-tertiary)]" data-testid="quiet-hours-status">
        {cfg.is_quiet_now
          ? "🌙 Du är i tysta timmar just nu — notiser köas till kl. " + String(cfg.quiet_hours_end).padStart(2, "0") + ":05."
          : "🔔 Push-notiser går igenom som vanligt just nu."}
      </p>
    </>
  );
}
